import React, { useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import api from "../../../../utils/api";
import { updateLeadsListCache } from "../actions/updateLeadsListCache";
import { getAllUrlParams } from "../../../../utils/helpers/setAndGetUrlParams";
import { useSelector } from "react-redux";
import { generateQueryParams } from "../../../../utils/helpers/generateQueryParams";
import { useCachedLeadData } from "../hooks/getCachedLeadData";

const CALL_TYPES = {
  INFORMATION: "information_call",
  JOINING: "joining_process_call",
  SERVICE_FIRST: "service_call_one",
  SERVICE_SECOND: "service_call_second",
  SERVICE_THIRD: "service_call_third",
};

const ITEMS_PER_PAGE = 3;

const CallDetailsForm = ({ data, onSuccess }) => {
  const [callType, setCallType] = useState("");
  const [remark, setRemark] = useState("");

  const [forceReload, setForceReload] = useState(false);
  const { leadData: enrollmentData } = useCachedLeadData(
    data._id,
    null,
    forceReload
  );
  const [pastEvents, setPastEvents] = useState([
    ...(enrollmentData?.callDetailsWithRemarks || []),
  ]);

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [currentPage, setCurrentPage] = useState(1);

  const urlParams = getAllUrlParams();
  const { filters } = useSelector((state) => state.filter);
  const leadsFilters = filters.leads || [];
  let { queryParams, params } = generateQueryParams(leadsFilters);

  const handleRemarkChange = (value) => setRemark(value);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!callType || !remark.trim()) {
      setMessage({
        type: "danger",
        text: "Please select call type and add remarks.",
      });
      return;
    }

    setLoading(true);
    setMessage({ type: "", text: "" });

    try {
      const callDetailsObject = {
        callType,
        callDate: new Date(),
        remarks: remark.trim(),
      };

      const payload = {
        callDetailsWithRemarks: [
          ...enrollmentData?.callDetailsWithRemarks,
          callDetailsObject,
        ],
      };
      console.log(
        "enrollent data",
        enrollmentData,
        enrollmentData.callDetailsWithRemarks,
        callDetailsObject
      );

      switch (callType) {
        case CALL_TYPES.INFORMATION:
          payload.informationCallDate = new Date();
          break;
        case CALL_TYPES.JOINING:
          payload.joiningProcessCallDate = new Date();
          break;
        case CALL_TYPES.SERVICE_FIRST:
          payload.serviceFirstCallOn = new Date();
          break;
        case CALL_TYPES.SERVICE_SECOND:
          payload.serviceSecondCallOn = new Date();
          break;
        case CALL_TYPES.SERVICE_THIRD:
          payload.serviceThirdCallOn = new Date();
          break;
        default:
          break;
      }

      console.log("payloadis", payload);

      const response = await api.patch(
        `/enrollment/${enrollmentData._id}`,
        payload
      );

      if (response.status === 200) {
        setMessage({
          type: "success",
          text: "Call details saved successfully.",
        });
        setCallType("");
        setRemark("");
        setPastEvents((prev) => [callDetailsObject, ...prev]);
        setCurrentPage(1); // Reset to first page
        if (onSuccess) onSuccess(response.data);
        console.log("UPDATING", response.data.data);

        updateLeadsListCache({
          id: enrollmentData._id,
          updatedData: response.data.data,
          page: urlParams?.page * 1,
          limit: urlParams?.limit * 1,
          queryString: params?.toString(),
          queryParams: queryParams?.toString(),
          companyId: urlParams?.companyid,
        });
      }
    } catch (err) {
      console.error("Error saving call details:", err);
      setMessage({
        type: "danger",
        text: err.response?.data?.message || "Failed to save call details.",
      });
    } finally {
      setLoading(false);
      setCallType("");
      setRemark("");
    }
  };

  // Pagination logic
  const totalPages = Math.ceil(pastEvents.length / ITEMS_PER_PAGE);
  const startIdx = (currentPage - 1) * ITEMS_PER_PAGE;
  const currentEvents = pastEvents.slice(startIdx, startIdx + ITEMS_PER_PAGE);

  return (
    <div className=" mb-4 p-0">
      <div className="card shadow-sm border-0 rounded-3">
        <div className="card-body ">
          <div className="row g-4">
            {/* ===== LEFT SIDE - FORM ===== */}
            <div className="col-lg-6 col-md-12 border-end">
              <h5 className="fw-semibold mb-3 text-dark">
                Log New Call Details
              </h5>

              {message.text && (
                <div className={`alert alert-${message.type}`} role="alert">
                  {message.text}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="d-flex flex-column gap-3 ">
                  {/* Call Type */}
                  <div>
                    <label className="form-label fw-semibold">Call Type</label>
                    <select
                      className="form-select"
                      value={callType}
                      onChange={(e) => setCallType(e.target.value)}
                      required
                    >
                      <option value="">Select Call Type</option>
                      <option value={CALL_TYPES.INFORMATION}>
                        Information Call
                      </option>
                      <option value={CALL_TYPES.JOINING}>
                        Joining Process Call
                      </option>
                      <option value={CALL_TYPES.SERVICE_FIRST}>
                        Service Call One
                      </option>
                      <option value={CALL_TYPES.SERVICE_SECOND}>
                        Service Call Two
                      </option>
                      <option value={CALL_TYPES.SERVICE_THIRD}>
                        Service Call Three
                      </option>
                    </select>
                  </div>

                  {/* Remarks */}
                  <div>
                    <label className="form-label fw-semibold">Remarks</label>
                    <textarea
                      className="form-control"
                      value={remark}
                      onChange={(e) => handleRemarkChange(e.target.value)}
                      rows={3}
                      required
                      placeholder="Enter your remarks here..."
                    ></textarea>
                  </div>
                </div>

                <button
                  type="submit"
                  className="btn btn-primary mt-4 px-4 ms-auto d-flex"
                  disabled={loading}
                >
                  {loading ? "Saving..." : "Save Call Details"}
                </button>
              </form>
            </div>

            {/* ===== RIGHT SIDE - PAST EVENTS ===== */}
            <div className="col-lg-6 col-md-12 ">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h5 className="fw-semibold text-dark">Past Call Details</h5>
                {totalPages > 1 && (
                  <div className="d-flex justify-content-between align-items-center">
                    <button
                      className="btn text-white btn-primary btn-sm"
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage((p) => p - 1)}
                    >
                      ←
                    </button>
                    <span className="text-muted small mx-2">
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      className="btn text-white btn-primary btn-sm"
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage((p) => p + 1)}
                    >
                      →
                    </button>
                  </div>
                )}
              </div>
              {pastEvents.length === 0 ? (
                <p className="text-muted fst-italic">
                  No past call records found.
                </p>
              ) : (
                <>
                  <div
                    className="overflow-auto"
                    style={{ maxHeight: "350px", scrollbarWidth: "thin" }}
                  >
                    {currentEvents.map((event, idx) => (
                      <div
                        key={idx}
                        className="border  border-end-3 border-top-0 border-bottom-0 border-start-2 border-primary rounded p-3 mb-3 bg-body-tertiary"
                      >
                        <div className="d-flex justify-content-between">
                          <strong className="text-capitalize text-dark">
                            {event.callType.replaceAll("_", " ")}
                          </strong>
                          <small className="text-muted">
                            {new Date(event.callDate).toLocaleString()}
                          </small>
                        </div>
                        <p className="mb-0 mt-2 text-black-80">
                          {event.remarks}
                        </p>
                      </div>
                    ))}
                  </div>

                  {/* Pagination controls */}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CallDetailsForm;
